/**
 * User menu popup with Socials, Mailbox, and Dungeons tabs.
 */

import type { SpaceTimeClient } from '../../spacetime/SpaceTimeClient';
import { t } from '../../i18n';
import { createTextInputPopup } from '../TextInputPopup';
import { createMailboxTabButton, createSocialTabButton, createTopTabButton } from './domFactories';
import { renderMailboxTab as renderMailboxTabSection } from './mailboxTab';
import { createTradeComposerManager } from './tradeComposerManager';
import { createTradeReviewManager } from './tradeReviewManager';
import { mapMailboxRows } from './mappers';
import { renderDungeonsTab as renderDungeonsTabSection } from './dungeonsTab';
import { renderSocialTab as renderSocialTabSection } from './socialTab';
import { restoreUserMenuTab } from './stateRestore';
import {
    setSubHeaderVisibility,
    updateMailboxTabLabels,
    updateMailboxTabStyles,
    updateSocialTabStyles,
    updateTopTabStyles,
    type UserMenuTabElements,
} from './tabsState';
import { createWindowFrame, windowId } from '../windowing';
import type {
    FriendDisplay,
    GuildMemberDisplay,
    MailboxDisplay,
    MailboxTab,
    SocialTab,
    UserMenuPopupHandle,
    UserMenuPopupOptions,
    UserMenuTab,
} from './types';

export type { UserMenuPopupHandle, UserMenuPopupOptions };

export function createUserMenuPopup(
    spaceTimeClient: SpaceTimeClient,
    options: UserMenuPopupOptions = {}
): UserMenuPopupHandle {
    const dungeons = options.dungeons ?? [];
    let destroyed = false;

    // State
    let isOpen = false;
    let activeMenuTab: UserMenuTab = 'Socials';
    let activeSocialTab: SocialTab = 'Friends';
    let activeMailboxTab: MailboxTab = 'Messages';
    let friendRows: FriendDisplay[] = [];
    let guildMembers: GuildMemberDisplay[] = [];
    let guildName: string | null = null;
    let messageRows: MailboxDisplay[] = [];
    let incomingTradeRows: MailboxDisplay[] = [];
    let outgoingTradeRows: MailboxDisplay[] = [];
    let unreadMessages = 0;
    let unreadIncomingTrades = 0;
    let enteringDungeonId: string | null = null;
    let lastRenderedMenuTab: UserMenuTab | null = null;
    const tabDirty: Record<UserMenuTab, boolean> = {
        Socials: true,
        Mailbox: true,
        Trades: true,
        Dungeons: true,
    };
    const tabFooterContent: Partial<Record<UserMenuTab, HTMLDivElement>> = {};
    const tabFooterVisible: Partial<Record<UserMenuTab, boolean>> = {};
    const initialSize = {
        width: Math.max(300, Math.round(window.innerWidth * 0.5)),
        height: Math.max(400, Math.round(window.innerHeight * 0.5)),
    };
    const initialPosition = {
        x: Math.round((window.innerWidth - initialSize.width) / 2),
        y: Math.round((window.innerHeight - initialSize.height) / 2),
    };

    const MIN_WIDTH = 300;
    const MIN_HEIGHT = 400;
    const fetchInventoryByPlayerId = (playerId: string) => spaceTimeClient.social.fetchInventoryByPlayerId(playerId);

    // Child popup
    const inputPopup = createTextInputPopup();

    // Create container
    const container = document.createElement('div');
    container.className = 'user-menu-popup window-container';
    Object.assign(container.style, {
        position: 'fixed',
        left: `${initialPosition.x}px`,
        top: `${initialPosition.y}px`,
        width: `${initialSize.width}px`,
        height: `${initialSize.height}px`,
        display: 'none',
    });

    // Header
    const header = document.createElement('div');
    header.className = 'window-header';

    const headerTitle = document.createElement('div');
    headerTitle.className = 'window-header-title';
    headerTitle.textContent = t('userMenu.title');

    const resizeHandle = document.createElement('div');
    resizeHandle.className = 'window-resize-handle';
    resizeHandle.innerHTML = '<svg width="10" height="10" viewBox="0 0 10 10" style="pointer-events: none;"><path d="M0 0 L10 0 L10 10 Z" fill="#9ca3af" /></svg>';

    header.appendChild(headerTitle);
    header.appendChild(resizeHandle);

    // Close button
    const closeBtn = document.createElement('button');
    closeBtn.textContent = '×';
    closeBtn.type = 'button';
    Object.assign(closeBtn.style, {
        marginLeft: 'auto',
        backgroundColor: 'transparent',
        border: 'none',
        color: '#9ca3af',
        fontSize: '1.5rem',
        cursor: 'pointer',
        lineHeight: '1',
        zIndex: '20',
    });
    closeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        toggle(false);
    });
    header.appendChild(closeBtn);

    const topTabBar = document.createElement('div');
    topTabBar.className = 'chat-box-tabs';
    Object.assign(topTabBar.style, {
        display: 'flex',
        background: 'rgba(0, 0, 0, 0.2)',
        borderBottom: '1px solid #2a3142',
    });

    const socialsTopTab = createTopTabButton('Socials', t('userMenu.tabs.socials'), setActiveMenuTab);
    const mailboxTopTab = createTopTabButton('Mailbox', t('userMenu.tabs.mailbox'), setActiveMenuTab);
    const tradesTopTab = createTopTabButton('Trades', t('userMenu.tabs.trades'), setActiveMenuTab);
    const dungeonsTopTab = createTopTabButton('Dungeons', t('userMenu.tabs.dungeons'), setActiveMenuTab);
    topTabBar.appendChild(socialsTopTab);
    topTabBar.appendChild(mailboxTopTab);
    topTabBar.appendChild(tradesTopTab);
    topTabBar.appendChild(dungeonsTopTab);

    const socialSubHeader = document.createElement('div');
    socialSubHeader.className = 'chat-box-tabs';
    Object.assign(socialSubHeader.style, {
        display: 'flex',
        background: 'rgba(0, 0, 0, 0.12)',
        borderBottom: '1px solid #2a3142',
    });

    const friendsTab = createSocialTabButton('Friends', t('userMenu.socials.tabs.friends'), setActiveSocialTab);
    const guildTab = createSocialTabButton('Guild', t('userMenu.socials.tabs.guild'), setActiveSocialTab);
    socialSubHeader.appendChild(friendsTab);
    socialSubHeader.appendChild(guildTab);

    const mailboxSubHeader = document.createElement('div');
    mailboxSubHeader.className = 'chat-box-tabs';
    Object.assign(mailboxSubHeader.style, {
        display: 'none',
        background: 'rgba(0, 0, 0, 0.12)',
        borderBottom: '1px solid #2a3142',
    });

    const messagesTab = createMailboxTabButton('Messages', t('userMenu.mailbox.tabs.messages'), setActiveMailboxTab);
    const incomingTradesTab = createMailboxTabButton('IncomingTrades', t('userMenu.mailbox.tabs.incomingTrades'), setActiveMailboxTab);
    const outgoingTradesTab = createMailboxTabButton('OutgoingTrades', t('userMenu.mailbox.tabs.outgoingTrades'), setActiveMailboxTab);
    mailboxSubHeader.appendChild(messagesTab);
    
    const tradesSubHeader = document.createElement('div');
    tradesSubHeader.className = 'chat-box-tabs';
    Object.assign(tradesSubHeader.style, {
        display: 'none',
        background: 'rgba(0, 0, 0, 0.12)',
        borderBottom: '1px solid #2a3142',
    });
    tradesSubHeader.appendChild(incomingTradesTab);
    tradesSubHeader.appendChild(outgoingTradesTab);

    // Content area
    const content = document.createElement('div');
    content.className = 'window-content';
    const socialPanel = document.createElement('div');
    const mailboxPanel = document.createElement('div');
    const tradesPanel = document.createElement('div');
    const dungeonsPanel = document.createElement('div');
    [socialPanel, mailboxPanel, tradesPanel, dungeonsPanel].forEach((panel) => {
        Object.assign(panel.style, { display: 'none', height: '100%' });
        content.appendChild(panel);
    });

    // Footer
    const footer = document.createElement('div');
    footer.className = 'window-footer';

    container.appendChild(header);
    container.appendChild(topTabBar);
    container.appendChild(socialSubHeader);
    container.appendChild(mailboxSubHeader);
    container.appendChild(tradesSubHeader);
    container.appendChild(content);
    container.appendChild(footer);
    document.body.appendChild(container);

    const tabElements: UserMenuTabElements = {
        socialSubHeader,
        mailboxSubHeader,
        tradesSubHeader,
        socialsTopTab,
        mailboxTopTab,
        tradesTopTab,
        dungeonsTopTab,
        friendsTab,
        guildTab,
        messagesTab,
        incomingTradesTab,
        outgoingTradesTab,
    };

    const windowFrame = createWindowFrame({
        windowId: windowId('user-menu', 'main'),
        container,
        header,
        resizeHandle,
        closeButton: closeBtn,
        minWidth: MIN_WIDTH,
        minHeight: MIN_HEIGHT,
        initialPosition,
        initialSize,
    });

    function invalidateTab(tab: UserMenuTab): void {
        tabDirty[tab] = true;
    }

    function invalidateTradeTabs(): void {
        invalidateTab('Trades');
        invalidateTab('Mailbox');
    }

    function setActiveMenuTab(tab: UserMenuTab): void {
        activeMenuTab = tab;
        windowFrame.setActiveTab(tab);
        const previousMailboxTab = activeMailboxTab;
        if (activeMenuTab === 'Mailbox') {
            activeMailboxTab = 'Messages';
        } else if (activeMenuTab === 'Trades' && activeMailboxTab === 'Messages') {
            activeMailboxTab = 'IncomingTrades';
        }
        if ((activeMenuTab === 'Mailbox' || activeMenuTab === 'Trades') && previousMailboxTab !== activeMailboxTab) {
            invalidateTradeTabs();
        }
        setSubHeaderVisibility(tabElements, activeMenuTab);
        if (activeMenuTab === 'Mailbox') {
            markActiveMailboxTabRead();
        }
        updateTopTabStyles(tabElements, activeMenuTab, unreadMessages, unreadIncomingTrades);
        render();
    }

    function setActiveSocialTab(tab: SocialTab): void {
        activeSocialTab = tab;
        invalidateTab('Socials');
        updateSocialTabStyles(tabElements, activeSocialTab);
        render();
    }

    function setActiveMailboxTab(tab: MailboxTab): void {
        activeMailboxTab = tab;
        tradeReviewManager.clearForMailboxTab(tab);
        invalidateTradeTabs();
        markActiveMailboxTabRead();
        updateMailboxTabStyles(tabElements, activeMailboxTab, unreadMessages, unreadIncomingTrades);
        render();
    }

    function markActiveMailboxTabRead(): void {
        if (activeMailboxTab === 'Messages' && unreadMessages > 0) {
            void spaceTimeClient.social.markMessagesRead();
        }
    }

    function toggle(open?: boolean): void {
        isOpen = open ?? !isOpen;
        container.style.display = isOpen ? 'flex' : 'none';
        windowFrame.setWindowState(isOpen ? 'open' : 'closed');
        if (!isOpen) return;
        setSubHeaderVisibility(tabElements, activeMenuTab);
        render();
    }

    function open(tab: UserMenuTab = 'Socials'): void {
        setActiveMenuTab(tab);
        toggle(true);
    }

    async function openTradeWith(targetName: string): Promise<void> {
        const normalizedTarget = targetName.trim();
        if (!normalizedTarget) return;
        open('Trades');
        setActiveMailboxTab('OutgoingTrades');
        await openTradeComposer(normalizedTarget);
    }

    function setActivePanelVisibility(): void {
        socialPanel.style.display = activeMenuTab === 'Socials' ? 'block' : 'none';
        mailboxPanel.style.display = activeMenuTab === 'Mailbox' ? 'block' : 'none';
        tradesPanel.style.display = activeMenuTab === 'Trades' ? 'block' : 'none';
        dungeonsPanel.style.display = activeMenuTab === 'Dungeons' ? 'block' : 'none';
    }

    function applyFooterForActiveTab(): void {
        footer.innerHTML = '';
        const visible = tabFooterVisible[activeMenuTab] ?? true;
        footer.style.display = visible ? 'flex' : 'none';
        if (!visible) return;
        const footerEl = tabFooterContent[activeMenuTab];
        if (footerEl) {
            footer.appendChild(footerEl);
        }
    }

    function renderSocialPanel(): void {
        socialPanel.innerHTML = '';
        const list = document.createElement('div');
        const footerContent = document.createElement('div');
        footerContent.style.display = 'contents';
        Object.assign(list.style, { display: 'flex', flexDirection: 'column', gap: '8px' });
        renderSocialTabSection({
            list,
            footer: footerContent,
            activeSocialTab,
            friendRows,
            guildName,
            guildMembers,
            inputPopup,
            spaceTimeClient,
            handleRemoveFriend,
            handleLeaveGuild,
        });
        socialPanel.appendChild(list);
        tabFooterContent['Socials'] = footerContent;
        tabFooterVisible['Socials'] = true;
    }

    function renderMailboxPanel(target: 'Mailbox' | 'Trades'): void {
        const panel = target === 'Mailbox' ? mailboxPanel : tradesPanel;
        panel.innerHTML = '';
        const tradeReviewState = tradeReviewManager.getState();

        const list = document.createElement('div');
        const footerContent = document.createElement('div');
        footerContent.style.display = 'contents';
        Object.assign(list.style, { display: 'flex', flexDirection: 'column', gap: '8px' });
        renderMailboxTabSection({
            list,
            footer: footerContent,
            inputPopup,
            state: {
                activeMailboxTab,
                messageRows,
                incomingTradeRows,
                outgoingTradeRows,
                incomingTradeReview: tradeReviewState.incomingTradeReview,
                incomingTradeReviewItems: tradeReviewState.incomingTradeReviewItems,
                outgoingTradeReview: tradeReviewState.outgoingTradeReview,
                outgoingTradeReviewItems: tradeReviewState.outgoingTradeReviewItems,
            },
            actions: {
                openTradeComposer,
                openIncomingTradeReview: tradeReviewManager.openIncoming,
                openOutgoingTradeReview: tradeReviewManager.openOutgoing,
                handleDeleteMail,
                handleCancelOutgoingTrade,
                handleAcceptTrade,
                handleRejectTrade,
                closeIncomingTradeReview: () => {
                    tradeReviewManager.close('incoming');
                },
                closeOutgoingTradeReview: () => {
                    tradeReviewManager.close('outgoing');
                },
            },
        });

        tabFooterVisible[target] = true;
        panel.appendChild(list);
        tabFooterContent[target] = footerContent;
    }

    function renderDungeonsPanel(): void {
        dungeonsPanel.innerHTML = '';
        const list = document.createElement('div');
        Object.assign(list.style, { display: 'flex', flexDirection: 'column', gap: '8px' });
        renderDungeonsTabSection({
            list,
            dungeons,
            enteringDungeonId,
            onEnter: handleEnterDungeon,
        });
        dungeonsPanel.appendChild(list);
        tabFooterVisible['Dungeons'] = false;
    }

    function render(): void {
        const tabChanged = lastRenderedMenuTab !== activeMenuTab;
        if (tabChanged) {
            setActivePanelVisibility();
        }
        if (!tabDirty[activeMenuTab] && !tabChanged) return;
        if (!tabDirty[activeMenuTab] && tabChanged) {
            applyFooterForActiveTab();
            lastRenderedMenuTab = activeMenuTab;
            return;
        }
        if (activeMenuTab === 'Socials') {
            renderSocialPanel();
        } else if (activeMenuTab === 'Mailbox') {
            renderMailboxPanel('Mailbox');
        } else if (activeMenuTab === 'Trades') {
            renderMailboxPanel('Trades');
        } else {
            renderDungeonsPanel();
        }
        tabDirty[activeMenuTab] = false;
        applyFooterForActiveTab();
        lastRenderedMenuTab = activeMenuTab;
    }

    const tradeComposerManager = createTradeComposerManager({
        spaceTimeClient,
        inputPopup,
        onTradesChanged: () => {
            invalidateTab('Trades');
            if (isOpen) render();
        },
    });
    const { openTradeComposer } = tradeComposerManager;
    const tradeReviewManager = createTradeReviewManager({
        fetchInventoryByPlayerId,
        markMailRead: (mailId) => spaceTimeClient.social.markMailRead(mailId),
        onChanged: () => {
            invalidateTab('Trades');
            render();
        },
    });

    async function handleEnterDungeon(dungeonId: string): Promise<void> {
        if (enteringDungeonId) return;
        enteringDungeonId = dungeonId;
        invalidateTab('Dungeons');
        render();
        try {
            if (options.onDungeonEnter) {
                await options.onDungeonEnter(dungeonId);
            } else {
                await spaceTimeClient.dungeon.enterDungeon(dungeonId);
            }
            toggle(false);
        } catch (e) {
            const message = e instanceof Error ? e.message : String(e);
            alert(t('userMenu.dungeons.enterFailed', { error: message }));
        } finally {
            enteringDungeonId = null;
            invalidateTab('Dungeons');
            if (isOpen) render();
        }
    }

    async function runConfirmedAction(
        confirmMessage: string,
        action: () => Promise<void>,
        onError?: (error: unknown) => void
    ): Promise<boolean> {
        if (!window.confirm(confirmMessage)) return false;
        try {
            await action();
            return true;
        } catch (error) {
            onError?.(error);
            return false;
        }
    }

    async function handleAcceptTrade(mailId: number): Promise<boolean> {
        return runConfirmedAction(
            t('userMenu.mailbox.acceptConfirm'),
            () => spaceTimeClient.social.acceptMailboxTrade(mailId),
            (error) => {
                alert(t('userMenu.mailbox.acceptFailed', { error: String(error) }));
            }
        );
    }

    async function handleRejectTrade(mailId: number): Promise<boolean> {
        return runConfirmedAction(
            t('userMenu.mailbox.rejectConfirm'),
            () => spaceTimeClient.social.deleteMailboxItem(mailId),
            (error) => {
                alert(t('userMenu.mailbox.rejectFailed', { error: String(error) }));
            }
        );
    }

    async function handleDeleteMail(mailId: number): Promise<void> {
        await runConfirmedAction(
            t('userMenu.mailbox.deleteConfirm'),
            () => spaceTimeClient.social.deleteMailboxItem(mailId),
            (error) => {
                alert(t('userMenu.mailbox.deleteFailed', { error: String(error) }));
            }
        );
    }

    async function handleCancelOutgoingTrade(mailId: number): Promise<void> {
        await runConfirmedAction(
            t('userMenu.mailbox.cancelConfirm'),
            () => spaceTimeClient.social.cancelOutgoingTrade(mailId),
            (error) => {
                alert(t('userMenu.mailbox.cancelFailed', { error: String(error) }));
            }
        );
    }

    async function handleRemoveFriend(name: string): Promise<void> {
        await runConfirmedAction(
            t('userMenu.socials.removeFriendConfirm', { name }),
            () => spaceTimeClient.social.removeFriend(name),
            (error) => {
                console.error('Remove friend failed', error);
            }
        );
    }

    async function handleLeaveGuild(): Promise<void> {
        await runConfirmedAction(
            t('userMenu.guild.leaveConfirm'),
            () => spaceTimeClient.social.leaveGuild(),
            (error) => {
                console.error('Leave guild failed', error);
            }
        );
    }

    // Keyboard handler
    function handleKeyDown(e: KeyboardEvent): void {
        const target = e.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
            return;
        }

        if (e.key === 'Escape' && isOpen) {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation();
            toggle(false);
        }
    }

    window.addEventListener('keydown', handleKeyDown, true);

    // Subscriptions
    let fUnsubscribe: (() => void) | undefined;
    let gUnsubscribe: (() => void) | undefined;
    let msgUnsubscribe: (() => void) | undefined;
    let inTradeUnsubscribe: (() => void) | undefined;
    let otUnsubscribe: (() => void) | undefined;
    let unreadUnsubscribe: (() => void) | undefined;

    const assignSubscription = (
        setter: (unsubscribe: (() => void) | undefined) => void,
        unsubscribe: () => void
    ): void => {
        if (destroyed) {
            unsubscribe();
            return;
        }
        setter(unsubscribe);
    };

    void (async () => {
        const fSub = await spaceTimeClient.social.subscribeToFriendRoster((rows) => {
            if (destroyed) return;
            friendRows = rows.map((row) => ({
                id: row.playerId,
                name: row.displayName || row.playerId.substring(0, 8),
                status: row.isOnline ? 'Online' : 'Offline',
            }));
            invalidateTab('Socials');
            if (isOpen) render();
        });
        assignSubscription((unsubscribe) => {
            fUnsubscribe = unsubscribe;
        }, fSub.unsubscribe);

        const gSub = await spaceTimeClient.social.subscribeToGuildRoster((row) => {
            if (destroyed) return;
            if (row) {
                guildName = row.guildName;
                guildMembers = row.members.map((member) => ({
                    id: member.playerId,
                    name: member.displayName,
                    rank: member.rank,
                    status: member.isOnline ? 'Online' : 'Offline',
                }));
            } else {
                guildName = null;
                guildMembers = [];
            }
            invalidateTab('Socials');
            if (isOpen) render();
        });
        assignSubscription((unsubscribe) => {
            gUnsubscribe = unsubscribe;
        }, gSub.unsubscribe);

        const msgSub = await spaceTimeClient.social.subscribeToMailboxMessages((rows) => {
            if (destroyed) return;
            messageRows = mapMailboxRows(rows);
            invalidateTab('Mailbox');
            if (isOpen) render();
        });
        assignSubscription((unsubscribe) => {
            msgUnsubscribe = unsubscribe;
        }, msgSub.unsubscribe);

        const inTradeSub = await spaceTimeClient.social.subscribeToIncomingTrades((rows) => {
            if (destroyed) return;
            incomingTradeRows = mapMailboxRows(rows);
            invalidateTab('Trades');
            if (isOpen) render();
        });
        assignSubscription((unsubscribe) => {
            inTradeUnsubscribe = unsubscribe;
        }, inTradeSub.unsubscribe);

        const otSub = await spaceTimeClient.social.subscribeToOutgoingTrades((rows) => {
            if (destroyed) return;
            outgoingTradeRows = mapMailboxRows(rows);
            invalidateTab('Trades');
            if (isOpen) render();
        });
        assignSubscription((unsubscribe) => {
            otUnsubscribe = unsubscribe;
        }, otSub.unsubscribe);

        const unreadSub = await spaceTimeClient.social.subscribeToMailboxUnreadCounts((counts) => {
            if (destroyed) return;
            unreadMessages = counts?.unreadMessages ?? 0;
            unreadIncomingTrades = counts?.unreadIncomingTrades ?? 0;
            updateMailboxTabLabels(tabElements, unreadMessages, unreadIncomingTrades);
            updateTopTabStyles(tabElements, activeMenuTab, unreadMessages, unreadIncomingTrades);
            updateMailboxTabStyles(tabElements, activeMailboxTab, unreadMessages, unreadIncomingTrades);
            invalidateTab('Mailbox');
            invalidateTab('Trades');
            if (isOpen) render();
        });
        assignSubscription((unsubscribe) => {
            unreadUnsubscribe = unsubscribe;
        }, unreadSub.unsubscribe);

    })();

    updateMailboxTabLabels(tabElements, unreadMessages, unreadIncomingTrades);
    updateTopTabStyles(tabElements, activeMenuTab, unreadMessages, unreadIncomingTrades);
    updateSocialTabStyles(tabElements, activeSocialTab);
    updateMailboxTabStyles(tabElements, activeMailboxTab, unreadMessages, unreadIncomingTrades);
    setSubHeaderVisibility(tabElements, activeMenuTab);
    const restoredTopTab = restoreUserMenuTab(windowFrame.getActiveTab());
    if (restoredTopTab) {
        activeMenuTab = restoredTopTab;
        setSubHeaderVisibility(tabElements, activeMenuTab);
        updateTopTabStyles(tabElements, activeMenuTab, unreadMessages, unreadIncomingTrades);
    }
    if (windowFrame.getWindowState() === 'open') {
        toggle(true);
    } else {
        toggle(false);
    }

    return {
        open,
        close() {
            toggle(false);
        },
        isOpenOnTab(tab: UserMenuTab): boolean {
            return isOpen && activeMenuTab === tab;
        },
        openTradeWith,
        destroy() {
            destroyed = true;
            window.removeEventListener('keydown', handleKeyDown, true);
            windowFrame.destroy();
            fUnsubscribe?.();
            gUnsubscribe?.();
            msgUnsubscribe?.();
            inTradeUnsubscribe?.();
            otUnsubscribe?.();
            unreadUnsubscribe?.();
            tradeComposerManager.destroyAll();
            inputPopup.destroy();
            container.remove();
        },
    };
}
